import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Settings2 } from "lucide-react";
import { useCustomFields, useCustomFieldValues } from "@/hooks/use-custom-fields";
import { CustomFieldDefinition } from "@/types/custom-fields";

interface CustomFieldsSectionProps {
  stockItemId: number;
  isEditMode: boolean;
  onValuesChange?: (values: Record<string, string>) => void;
  onRegisterSaveHandler?: (fn: () => Promise<boolean>) => void;
  /** Whether to fetch from backend. Set to false in demo mode to avoid backend calls. Defaults to true. */
  isLiveMode?: boolean;
}

export function CustomFieldsSection({ stockItemId, isEditMode, onValuesChange, onRegisterSaveHandler, isLiveMode = true }: CustomFieldsSectionProps) {
  const { definitions, isLoading: isLoadingDefinitions } = useCustomFields({ enabled: isLiveMode });
  const { values, isLoading: isLoadingValues, bulkUpsertValues } = useCustomFieldValues(stockItemId, { enabled: isLiveMode });
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

  useEffect(() => {
    // Initialize field values from fetched data
    const initialValues: Record<string, string> = {};
    values.forEach((value) => {
      const def = definitions.find((d) => d.customFieldDefinitionID === value.customFieldDefinitionID);
      if (def) {
        initialValues[def.fieldName] = value.fieldValue;
      }
    });
    setFieldValues(initialValues);
  }, [values, definitions]);

  // Register save handler with parent component
  useEffect(() => {
    if (!onRegisterSaveHandler) return;

    const saveHandler = async (): Promise<boolean> => {
      if (!stockItemId) return true; // Nothing to save if no stock item ID

      // Build payload based on current definitions + fieldValues
      const payload = definitions
        .filter((def) => def.customFieldDefinitionID !== undefined)
        .map((def) => ({
          customFieldDefinitionID: def.customFieldDefinitionID!,
          fieldValue: fieldValues[def.fieldName] ?? "",
        }));

      if (payload.length === 0) {
        return true; // Nothing to save
      }

      return bulkUpsertValues(payload);
    };

    onRegisterSaveHandler(saveHandler);
  }, [definitions, fieldValues, stockItemId, onRegisterSaveHandler, bulkUpsertValues]);

  const handleValueChange = (fieldName: string, value: string) => {
    const newValues = { ...fieldValues, [fieldName]: value };
    setFieldValues(newValues);
    onValuesChange?.(newValues);
  };

  const renderField = (definition: CustomFieldDefinition) => {
    const value = fieldValues[definition.fieldName] || "";
    const fieldId = `custom-field-${definition.fieldName}`;

    if (!isEditMode) {
      // Display mode
      return (
        <div key={definition.customFieldDefinitionID} className="space-y-1">
          <Label className="text-sm font-medium">{definition.fieldLabel}</Label>
          <div className="text-sm">
            {definition.fieldType === "Boolean" ? (
              <Badge variant={value === "true" ? "default" : "secondary"}>{value === "true" ? "Yes" : "No"}</Badge>
            ) : definition.fieldType === "Date" && value ? (
              new Date(value).toLocaleDateString()
            ) : value ? (
              value
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </div>
        </div>
      );
    }

    // Edit mode
    switch (definition.fieldType) {
      case "Text":
        return (
          <div key={definition.customFieldDefinitionID} className="space-y-2">
            <Label htmlFor={fieldId}>
              {definition.fieldLabel}
              {definition.isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Input
              id={fieldId}
              value={value}
              onChange={(e) => handleValueChange(definition.fieldName, e.target.value)}
              placeholder={`Enter ${definition.fieldLabel.toLowerCase()}`}
            />
          </div>
        );

      case "Number":
        return (
          <div key={definition.customFieldDefinitionID} className="space-y-2">
            <Label htmlFor={fieldId}>
              {definition.fieldLabel}
              {definition.isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Input
              id={fieldId}
              type="number"
              value={value}
              onChange={(e) => handleValueChange(definition.fieldName, e.target.value)}
              placeholder={`Enter ${definition.fieldLabel.toLowerCase()}`}
            />
          </div>
        );

      case "Date":
        return (
          <div key={definition.customFieldDefinitionID} className="space-y-2">
            <Label htmlFor={fieldId}>
              {definition.fieldLabel}
              {definition.isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Input
              id={fieldId}
              type="date"
              value={value}
              onChange={(e) => handleValueChange(definition.fieldName, e.target.value)}
            />
          </div>
        );

      case "Dropdown":
        const options = definition.dropdownOptions ? JSON.parse(definition.dropdownOptions) : [];
        return (
          <div key={definition.customFieldDefinitionID} className="space-y-2">
            <Label htmlFor={fieldId}>
              {definition.fieldLabel}
              {definition.isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Select value={value} onValueChange={(val) => handleValueChange(definition.fieldName, val)}>
              <SelectTrigger id={fieldId}>
                <SelectValue placeholder={`Select ${definition.fieldLabel.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent>
                {options.map((option: string) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case "Boolean":
        return (
          <div key={definition.customFieldDefinitionID} className="flex items-center space-x-2">
            <Switch
              id={fieldId}
              checked={value === "true"}
              onCheckedChange={(checked) => handleValueChange(definition.fieldName, checked.toString())}
            />
            <Label htmlFor={fieldId} className="cursor-pointer">
              {definition.fieldLabel}
              {definition.isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
          </div>
        );

      default:
        return null;
    }
  };

  if (isLoadingDefinitions || isLoadingValues) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Custom Fields</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (definitions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Custom Fields</CardTitle>
          <CardDescription>No custom fields have been configured yet.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Settings2 className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              Configure custom fields in the settings to track additional stock item attributes.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Custom Fields</CardTitle>
        <CardDescription>Additional attributes specific to your industry</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{definitions.map((def) => renderField(def))}</div>
      </CardContent>
    </Card>
  );
}
